<script setup>
import TvScorebug from '~/components/tv/TvScorebug.vue';
import {TV_COMPETITION_ICON} from '~/utils/tvDisplay';

// The screen's one big moment: a result comes in and takes over for a few
// seconds. A club-colour wipe, the two sides slide in, the score counts up and
// the winner lights. A confirmed result gets confetti.
const props = defineProps({
  result: {type: Object, required: true},
  confirmed: {type: Boolean, default: false},
  accent: {type: String, default: '#D32946'},
  // Draw the confetti on this canvas instead of over the whole document: the
  // marketing page shows the screen inside a frame, and confetti over the
  // page copy looks like a bug. A wall screen leaves it unset.
  canvas: {type: Object, default: null},
});

const emit = defineEmits(['done']);

const HOLD_MS = 8000;
const reduced = import.meta.client && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const numeric = (text) => /^\d+$/.test(String(text ?? '').trim()) ? Number(text) : null;
const homeTarget = numeric(props.result.homeResult);
const awayTarget = numeric(props.result.awayResult);
const homeShown = ref(homeTarget != null && !reduced ? 0 : props.result.homeResult);
const awayShown = ref(awayTarget != null && !reduced ? 0 : props.result.awayResult);
const revealed = ref(reduced);

// The scorebug shows the counting score until it lands
const shownResult = computed(() => ({...props.result, homeResult: homeShown.value, awayResult: awayShown.value}));

let timers = [];
const later = (fn, ms) => timers.push(setTimeout(fn, ms));

const countUp = () => {
  const steps = Math.max(homeTarget ?? 0, awayTarget ?? 0);
  if (!steps) {
    homeShown.value = props.result.homeResult;
    awayShown.value = props.result.awayResult;
    return;
  }
  const stepMs = Math.min(260, 900 / steps);
  for (let i = 1; i <= steps; i++) {
    later(() => {
      if (homeTarget != null) homeShown.value = Math.min(i, homeTarget);
      if (awayTarget != null) awayShown.value = Math.min(i, awayTarget);
    }, i * stepMs);
  }
};

const fireConfetti = async () => {
  if (reduced) return;
  const module = (await import('canvas-confetti')).default;
  const confetti = props.canvas ? module.create(props.canvas, {resize: true, useWorker: false}) : module;
  const colors = [props.accent, '#ffffff', '#ffd23f'];
  confetti({particleCount: 140, spread: 75, startVelocity: 55, origin: {x: 0.2, y: 0.75}, angle: 60, colors});
  confetti({particleCount: 140, spread: 75, startVelocity: 55, origin: {x: 0.8, y: 0.75}, angle: 120, colors});
};

onMounted(() => {
  later(() => {
    revealed.value = true;
    later(countUp, 500);
    if (props.confirmed) later(fireConfetti, 1300);
  }, reduced ? 0 : 700);
  later(() => emit('done'), HOLD_MS);
});

onUnmounted(() => timers.forEach(clearTimeout));
</script>

<template>
  <div class="tv-sting" :class="{'tv-sting--revealed': revealed, 'tv-sting--reduced': reduced}">
    <div class="tv-sting-wipe"/>
    <div class="tv-sting-card">
      <div class="tv-sting-head">
        <span class="tv-sting-kicker">{{ confirmed ? 'Result confirmed' : 'Result in' }}</span>
        <span class="tv-sting-comp">
          <v-icon :icon="TV_COMPETITION_ICON[result.competitionKind]" size="40"/>
          {{ result.competitionName }}
        </span>
      </div>
      <div class="tv-sting-board">
        <tv-scorebug :result="shownResult" size="hero" class="tv-sting-bug"/>
      </div>
      <div v-if="!confirmed" class="tv-sting-note">Waiting for the other side to confirm</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tv-sting {
  position: absolute;
  inset: 0;
  z-index: 50;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* The wipe: a slanted accent band sweeps across, then stays as the backdrop */
.tv-sting-wipe {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -30%;
  width: 160%;
  background: var(--tv-accent);
  transform: translateX(-110%) skewX(-14deg);
  animation: tv-wipe 0.7s cubic-bezier(.7, 0, .2, 1) forwards;
}

.tv-sting-card {
  position: relative;
  width: 1640px;
  padding: 64px 72px 72px;
  border-radius: 12px;
  background: var(--tv-sting-card);
  color: var(--tv-sting-ink);
  box-shadow: 0 40px 80px -30px rgba(0, 0, 0, 0.45);
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.35s ease, transform 0.5s cubic-bezier(.2, 1.2, .4, 1);
}

.tv-sting--revealed .tv-sting-card {
  opacity: 1;
  transform: none;
}

.tv-sting-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  padding-bottom: 36px;
  border-bottom: 2px solid var(--tv-sting-line);
}

.tv-sting-kicker {
  font-family: var(--tv-display);
  font-weight: 800;
  font-size: 60px;
  color: var(--tv-accent-text);
}

.tv-sting-comp {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 34px;
  color: var(--tv-sting-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tv-sting-board {
  display: flex;
  justify-content: center;
  margin-top: 56px;
}

/* The hero scorebug slides in once the card is up */
.tv-sting-bug {
  width: 100%;
  opacity: 0;
  transform: translateX(-80px);
  transition: transform 0.6s cubic-bezier(.2, 1, .3, 1) 0.15s, opacity 0.4s 0.15s;
}

.tv-sting--revealed .tv-sting-bug {
  opacity: 1;
  transform: none;
}

.tv-sting-note {
  margin-top: 36px;
  text-align: center;
  font-size: 30px;
  color: var(--tv-sting-muted);
}

@keyframes tv-wipe {
  to { transform: translateX(0) skewX(-14deg); }
}

.tv-sting--reduced .tv-sting-wipe {
  animation: none;
  transform: translateX(0) skewX(-14deg);
}

.tv-sting--reduced .tv-sting-card,
.tv-sting--reduced .tv-sting-bug {
  transition: none;
}

/* Portrait: the sides stack above and below the score */
:global(.tv-portrait .tv-sting-card) {
  width: 940px;
  padding: 56px 48px 64px;
}

:global(.tv-portrait .tv-sting-head) {
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

:global(.tv-portrait .tv-sting-bug) {
  --bug-font: 34px;
  --bug-score: 44px;
  --bug-cell: 60px;
  --bug-row: 64px;
}
</style>
